/**
 * ClassroomIcon — the single render helper for a Salón's visual identity
 * (PRD-30 §FR-9). Resolves `effective_icon` through the name→component registry
 * and tints it with the deterministic subject accent.
 *
 * Decorative by contract: rendered `aria-hidden` — the Salón name remains the
 * accessible label (accessibility.md §5, PRD-30 §FR-13). Default size is
 * h-4 w-4; override via `className`.
 */

import { cn } from "@lib/cn"

import { accentForSubject, iconComponentFor } from "@/constants/classroomIcons"
import type { Classroom } from "@/types"

interface Props {
  classroom: Pick<Classroom, "effective_icon" | "subject_default_code">
  className?: string
  /** When false, render without the subject accent tint. Defaults to true. */
  tinted?: boolean
}

export function ClassroomIcon({ classroom, className, tinted = true }: Props) {
  const Icon = iconComponentFor(classroom.effective_icon)
  return (
    <Icon
      aria-hidden
      className={cn(
        "h-4 w-4",
        tinted && accentForSubject(classroom.subject_default_code ?? ""),
        className,
      )}
    />
  )
}
